import { useCallback, useEffect, useState } from 'react';
import { getRequest } from './requestDetailApi';

function requestLoadMessage(error) {
  if (error?.status === 404) return 'Request not found';
  if (error?.status === 403) return 'Access denied';
  return error?.message || 'Failed to load request';
}

export default function useRequestDetailData(requestId) {
  const [request, setRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const refresh = useCallback(async ({ silent = false } = {}) => {
    try {
      if (!silent) setLoading(true);
      const data = await getRequest(requestId);
      setRequest(data);
      setError(null);
    } catch (err) {
      // A failed silent poll (first-pass status polling) must not replace a
      // fully-loaded page with the error screen -- one network blip mid-poll
      // used to do exactly that.
      if (!silent) setError(requestLoadMessage(err));
    } finally {
      if (!silent) setLoading(false);
    }
  }, [requestId]);

  // Stale-response guard for id changes: navigating between two request
  // details without unmounting must not let the slower fetch win.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const data = await getRequest(requestId);
        if (cancelled) return;
        setRequest(data);
        setError(null);
      } catch (err) {
        if (!cancelled) setError(requestLoadMessage(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [requestId]);

  useEffect(() => {
    if (!['queued', 'running'].includes(request?.firstPassStatus)) return undefined;
    const timer = setInterval(() => refresh({ silent: true }), 3000);
    return () => clearInterval(timer);
  }, [request?.firstPassStatus, refresh]);

  return {
    error,
    loading,
    refresh,
    request,
  };
}
